import React, { useState, useEffect, useRef } from "react";
import { AlertCircle, User, UserCheck } from "lucide-react";

interface Badge {
  id: string;
  type: "staff" | "patient";
  name: string;
  role?: string;
  x: number;
  y: number;
  targetX?: number;
  targetY?: number;
  status: "available" | "busy" | "emergency";
  speed: number;
}

interface Alarm {
  id: string;
  type: "pump" | "door" | "code-silver" | "wander";
  location: string;
  x: number;
  y: number;
  severity: "low" | "medium" | "high" | "critical";
  timestamp: Date;
}

interface Room {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: "patient" | "nurse-station" | "equipment" | "corridor";
}

const LocationAwareBlueDotMap: React.FC = () => {
  const [badges, setBadges] = useState<Badge[]>([
    {
      id: "1",
      type: "staff",
      name: "Dr. Smith",
      role: "Physician",
      x: 150,
      y: 100,
      status: "available",
      speed: 1.5,
    },
    {
      id: "2",
      type: "staff",
      name: "Nurse Johnson",
      role: "RN",
      x: 350,
      y: 200,
      status: "busy",
      speed: 2,
    },
    {
      id: "3",
      type: "patient",
      name: "Patient A",
      x: 100,
      y: 250,
      status: "available",
      speed: 0.5,
    },
    {
      id: "4",
      type: "patient",
      name: "Patient B",
      x: 450,
      y: 150,
      status: "available",
      speed: 0.8,
    },
    {
      id: "5",
      type: "staff",
      name: "Nurse Williams",
      role: "RN",
      x: 250,
      y: 350,
      status: "available",
      speed: 1.8,
    },
  ]);

  const [alarms, setAlarms] = useState<Alarm[]>([
    {
      id: "a1",
      type: "pump",
      location: "Room 203",
      x: 380,
      y: 120,
      severity: "medium",
      timestamp: new Date(),
    },
    {
      id: "a2",
      type: "door",
      location: "Exit B",
      x: 500,
      y: 300,
      severity: "low",
      timestamp: new Date(),
    },
  ]);

  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [activeCodeSilver, setActiveCodeSilver] = useState(false);
  const animationFrameRef = useRef<number>();

  const rooms: Room[] = [
    {
      id: "r1",
      name: "Room 201",
      x: 50,
      y: 50,
      width: 150,
      height: 120,
      type: "patient",
    },
    {
      id: "r2",
      name: "Room 202",
      x: 220,
      y: 50,
      width: 150,
      height: 120,
      type: "patient",
    },
    {
      id: "r3",
      name: "Room 203",
      x: 390,
      y: 50,
      width: 150,
      height: 120,
      type: "patient",
    },
    {
      id: "r4",
      name: "Nurse Station",
      x: 200,
      y: 200,
      width: 200,
      height: 100,
      type: "nurse-station",
    },
    {
      id: "r5",
      name: "Room 204",
      x: 50,
      y: 330,
      width: 150,
      height: 120,
      type: "patient",
    },
    {
      id: "r6",
      name: "Room 205",
      x: 220,
      y: 330,
      width: 150,
      height: 120,
      type: "patient",
    },
    {
      id: "r7",
      name: "Equipment",
      x: 390,
      y: 330,
      width: 150,
      height: 120,
      type: "equipment",
    },
  ];

  // Smooth badge movement with animation frames
  useEffect(() => {
    let lastTime = 0;

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      setBadges((prevBadges) =>
        prevBadges.map((badge) => {
          // Set new target position every few seconds
          if (
            !badge.targetX ||
            !badge.targetY ||
            (Math.abs(badge.x - badge.targetX) < 5 &&
              Math.abs(badge.y - badge.targetY) < 5)
          ) {
            // Find a random room or corridor position
            const targetRoom = rooms[Math.floor(Math.random() * rooms.length)];
            const targetX = targetRoom.x + Math.random() * targetRoom.width;
            const targetY = targetRoom.y + Math.random() * targetRoom.height;

            return {
              ...badge,
              targetX: Math.max(20, Math.min(580, targetX)),
              targetY: Math.max(20, Math.min(480, targetY)),
            };
          }

          // Move towards target
          const dx = badge.targetX! - badge.x;
          const dy = badge.targetY! - badge.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 1) {
            const moveDistance = badge.speed * deltaTime * 0.05;
            const ratio = Math.min(moveDistance / distance, 1);

            return {
              ...badge,
              x: badge.x + dx * ratio,
              y: badge.y + dy * ratio,
            };
          }

          return badge;
        })
      );

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const handleCodeSilver = () => {
    setActiveCodeSilver(true);
    const newAlarm: Alarm = {
      id: `cs-${Date.now()}`,
      type: "code-silver",
      location: "West Wing",
      x: 300,
      y: 250,
      severity: "critical",
      timestamp: new Date(),
    };
    setAlarms([...alarms, newAlarm]);

    // Make all available staff move towards the alarm
    setBadges((prevBadges) =>
      prevBadges.map((badge) => {
        if (badge.type === "staff" && badge.status === "available") {
          return {
            ...badge,
            targetX: newAlarm.x + (Math.random() - 0.5) * 50,
            targetY: newAlarm.y + (Math.random() - 0.5) * 50,
            status: "emergency" as const,
            speed: 3, // Increase speed during emergency
          };
        }
        return badge;
      })
    );

    // Clear after 10 seconds for demo
    setTimeout(() => {
      setActiveCodeSilver(false);
      setBadges((prevBadges) =>
        prevBadges.map((badge) => ({
          ...badge,
          status: badge.status === "emergency" ? "available" : badge.status,
          speed: badge.type === "staff" ? 1.5 : 0.8,
        }))
      );
      setAlarms((prev) => prev.filter((a) => a.type !== "code-silver"));
    }, 10000);
  };

  const getAlarmColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "text-red-600";
      case "high":
        return "text-orange-600";
      case "medium":
        return "text-yellow-600";
      default:
        return "text-blue-600";
    }
  };

  const findClosestStaff = (x: number, y: number) => {
    return badges
      .filter((b) => b.type === "staff" && b.status === "available")
      .map((staff) => ({
        ...staff,
        distance: Math.sqrt(
          Math.pow(staff.x - x, 2) + Math.pow(staff.y - y, 2)
        ),
      }))
      .sort((a, b) => a.distance - b.distance);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">
              Location-Aware Floor Map
            </h1>
            <button
              onClick={handleCodeSilver}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeCodeSilver
                  ? "bg-red-600 text-white animate-pulse"
                  : "bg-red-500 text-white hover:bg-red-600"
              }`}
            >
              Simulate Code Silver
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Map Area */}
            <div className="lg:col-span-3">
              <div
                className="relative bg-gray-100 rounded-lg overflow-hidden"
                style={{ height: "500px" }}
              >
                <svg width="600" height="500" className="absolute inset-0">
                  {/* Grid lines for visual reference */}
                  <defs>
                    <pattern
                      id="grid"
                      width="50"
                      height="50"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d="M 50 0 L 0 0 0 50"
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth="1"
                      />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />

                  {/* Rooms */}
                  {rooms.map((room) => (
                    <g key={room.id}>
                      <rect
                        x={room.x}
                        y={room.y}
                        width={room.width}
                        height={room.height}
                        fill={
                          room.type === "nurse-station"
                            ? "#DBEAFE"
                            : room.type === "equipment"
                            ? "#FEF3C7"
                            : "#F9FAFB"
                        }
                        stroke="#D1D5DB"
                        strokeWidth="2"
                        rx="4"
                      />
                      <text
                        x={room.x + room.width / 2}
                        y={room.y + room.height / 2}
                        textAnchor="middle"
                        className="text-xs fill-gray-600 font-medium"
                      >
                        {room.name}
                      </text>
                    </g>
                  ))}

                  {/* Alarms */}
                  {alarms.map((alarm) => (
                    <g key={alarm.id} className={getAlarmColor(alarm.severity)}>
                      <circle
                        cx={alarm.x}
                        cy={alarm.y}
                        r="20"
                        fill="currentColor"
                        fillOpacity="0.2"
                        className="animate-ping"
                      />
                      <circle
                        cx={alarm.x}
                        cy={alarm.y}
                        r="15"
                        fill="white"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <AlertCircle
                        x={alarm.x - 10}
                        y={alarm.y - 10}
                        size={20}
                        fill="currentColor"
                      />
                    </g>
                  ))}

                  {/* Badges */}
                  {badges.map((badge) => (
                    <g
                      key={badge.id}
                      onClick={() => setSelectedBadge(badge)}
                      className="cursor-pointer"
                      style={{
                        transform: `translate(${badge.x}px, ${badge.y}px)`,
                        transition: "none",
                      }}
                    >
                      <circle
                        cx={0}
                        cy={0}
                        r="14"
                        fill={badge.type === "staff" ? "#3B82F6" : "#10B981"}
                        fillOpacity={badge.status === "emergency" ? 1 : 0.9}
                        stroke={
                          selectedBadge?.id === badge.id
                            ? "#1F2937"
                            : badge.status === "emergency"
                            ? "#DC2626"
                            : "white"
                        }
                        strokeWidth={selectedBadge?.id === badge.id ? "3" : "2"}
                        className={
                          badge.status === "emergency" ? "animate-pulse" : ""
                        }
                      />
                      {badge.type === "staff" ? (
                        <UserCheck x={-8} y={-8} size={16} fill="white" />
                      ) : (
                        <User x={-8} y={-8} size={16} fill="white" />
                      )}
                      {/* Name label */}
                      <text
                        x={0}
                        y={25}
                        textAnchor="middle"
                        className="text-xs fill-gray-700 font-medium"
                      >
                        {badge.name.split(" ")[1] || badge.name}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            </div>

            {/* Status Panel */}
            <div className="space-y-4">
              {/* Legend */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3">Legend</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    <span>Staff</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span>Patient</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="text-red-600" />
                    <span>Critical Alarm</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="text-yellow-600" />
                    <span>Warning</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-600 rounded-full animate-pulse"></div>
                    <span>Emergency Response</span>
                  </div>
                </div>
              </div>

              {/* Active Alarms */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3">
                  Active Alarms ({alarms.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {alarms.map((alarm) => {
                    const closestStaff = findClosestStaff(alarm.x, alarm.y);
                    return (
                      <div
                        key={alarm.id}
                        className={`text-sm p-2 rounded border ${
                          alarm.severity === "critical"
                            ? "bg-red-50 border-red-200"
                            : alarm.severity === "high"
                            ? "bg-orange-50 border-orange-200"
                            : alarm.severity === "medium"
                            ? "bg-yellow-50 border-yellow-200"
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        <div className="font-medium flex items-center gap-1">
                          <AlertCircle size={14} />
                          {alarm.type.replace("-", " ").toUpperCase()}
                        </div>
                        <div className="text-gray-600">{alarm.location}</div>
                        {closestStaff.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Nearest: {closestStaff[0].name} (
                            {Math.round(closestStaff[0].distance)}m)
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selected Badge Info */}
              {selectedBadge && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Selected</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="font-medium">Name:</span>{" "}
                      {selectedBadge.name}
                    </div>
                    {selectedBadge.role && (
                      <div>
                        <span className="font-medium">Role:</span>{" "}
                        {selectedBadge.role}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Status:</span>{" "}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          selectedBadge.status === "emergency"
                            ? "bg-red-100 text-red-800"
                            : selectedBadge.status === "busy"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {selectedBadge.status}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Location:</span> (
                      {Math.round(selectedBadge.x)},{" "}
                      {Math.round(selectedBadge.y)})
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3">
                  Floor Stats
                </h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Staff on floor:</span>
                    <span className="font-medium">
                      {badges.filter((b) => b.type === "staff").length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Available staff:</span>
                    <span className="font-medium">
                      {
                        badges.filter(
                          (b) => b.type === "staff" && b.status === "available"
                        ).length
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Patients:</span>
                    <span className="font-medium">
                      {badges.filter((b) => b.type === "patient").length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Active alarms:</span>
                    <span
                      className={`font-medium ${
                        alarms.length > 0 ? "text-red-600" : ""
                      }`}
                    >
                      {alarms.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationAwareBlueDotMap;
